import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { CircleAlert, CircleCheck, X } from "@fg/icons";
import { cn } from "@/lib/utils";
import { errorMessage } from "@/api/driveApi";

type ToastVariant = "default" | "success" | "error";

export interface ToastOptions {
    message: string;
    variant?: ToastVariant;
    action?: { label: string; onClick: () => void };
    durationMs?: number;
}

interface ToastEntry extends Required<Pick<ToastOptions, "message" | "variant">> {
    id: number;
    action?: ToastOptions["action"];
    durationMs: number;
}

export interface ToastApi {
    show: (opts: ToastOptions) => number;
    success: (message: string, opts?: Omit<ToastOptions, "message" | "variant">) => number;
    error: (err: unknown, opts?: Omit<ToastOptions, "message" | "variant">) => number;
    dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
    return ctx;
}

const MAX_VISIBLE = 3;

/** Small in-house snackbar system (bottom-left, dark, optional Undo). */
export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<ToastEntry[]>([]);
    const nextId = useRef(1);

    const dismiss = useCallback((id: number) => {
        setToasts((list) => list.filter((t) => t.id !== id));
    }, []);

    const show = useCallback((opts: ToastOptions) => {
        const id = nextId.current++;
        const variant = opts.variant ?? "default";
        const durationMs = opts.durationMs ?? (opts.action ? 8000 : variant === "error" ? 7000 : 4500);
        setToasts((list) => [...list, { id, message: opts.message, variant, action: opts.action, durationMs }].slice(-MAX_VISIBLE));
        return id;
    }, []);

    const api = useMemo<ToastApi>(() => ({
        show,
        dismiss,
        success: (message, opts) => show({ ...opts, message, variant: "success" }),
        error: (err, opts) => show({ ...opts, message: typeof err === "string" ? err : errorMessage(err), variant: "error" })
    }), [show, dismiss]);

    return (
        <ToastContext.Provider value={api}>
            {children}
            <div
                aria-live="polite"
                aria-relevant="additions"
                className="pointer-events-none fixed bottom-4 left-1/2 z-[80] flex w-[min(26rem,calc(100vw-2rem))] -translate-x-1/2 flex-col gap-2 sm:left-4 sm:translate-x-0"
            >
                {toasts.map((t) => (
                    <ToastView key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
                ))}
            </div>
        </ToastContext.Provider>
    );
}

function ToastView({ toast, onDismiss }: { toast: ToastEntry; onDismiss: () => void }) {
    const [paused, setPaused] = useState(false);
    const dismissRef = useRef(onDismiss);
    dismissRef.current = onDismiss;

    useEffect(() => {
        if (paused) return;
        const t = window.setTimeout(() => dismissRef.current(), toast.durationMs);
        return () => window.clearTimeout(t);
    }, [paused, toast.durationMs]);

    return (
        <div
            role={toast.variant === "error" ? "alert" : "status"}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
            className="drive-slide-up pointer-events-auto flex items-center gap-3 rounded-lg bg-zinc-900 px-4 py-3 text-sm text-zinc-50 shadow-2xl ring-1 ring-black/10 dark:bg-zinc-100 dark:text-zinc-900"
        >
            {toast.variant === "success" ? (
                <CircleCheck className="h-4 w-4 shrink-0 text-emerald-400 dark:text-emerald-600" aria-hidden />
            ) : toast.variant === "error" ? (
                <CircleAlert className="h-4 w-4 shrink-0 text-red-400 dark:text-red-600" aria-hidden />
            ) : null}
            <span className="min-w-0 flex-1 break-words">{toast.message}</span>
            {toast.action ? (
                <button
                    type="button"
                    onClick={() => {
                        toast.action?.onClick();
                        onDismiss();
                    }}
                    className="shrink-0 rounded px-2 py-1 text-sm font-semibold text-sky-300 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-300 dark:text-sky-700 dark:hover:bg-black/5"
                >
                    {toast.action.label}
                </button>
            ) : null}
            <button
                type="button"
                onClick={onDismiss}
                aria-label="Dismiss"
                className={cn(
                    "-mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full opacity-70 hover:bg-white/10 hover:opacity-100 dark:hover:bg-black/5",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-300"
                )}
            >
                <X className="h-3.5 w-3.5" />
            </button>
        </div>
    );
}
